import * as React from 'react';
import { LeafletMap, LeafletMarkerLayer } from '@uncinc/react-leaflet';
import { TileLayer, ZoomControl } from 'react-leaflet';
import { LatLngExpression, Map } from 'leaflet';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { LeafletMarker } from '@uncinc/react-leaflet/dist/types';
import { MAP_ZOOM } from '../../config/constants';

import './style.scss';
import { WIDGET_OPTIONS } from '../../../../shared/config/constants';
import { AddressFull } from '../../../../shared/types/opzet';
import LeafletCenterControl from '../LeafletCenterControl';
import { Container, ContainerType } from '../../types';
import formatContainerMarkers from '../../helpers/formatContainerMarkers';
import { LeafletPopup, popupContentConstructor } from '../LeafletPopup';
import usePopup from '../../helpers/usePopup';

interface Props {
  address: AddressFull;
  containers: Container[];
  excluded: Container['type'][];
  setSelectedMarker: Function;
}

const LeafletMapRender = (props: Props) => {
  const {
    address,
    containers,
    excluded,
    setSelectedMarker,
  } = props;

  const { t } = useTranslation();
  const mapRef = React.useRef<Map>(null);
  const popup = usePopup(mapRef.current);
  const center: LatLngExpression = [address.latitude, address.longitude];
  const [markers, setMarkers] = React.useState<LeafletMarker[]>([]);

  React.useEffect(() => {
    const formattedContainers = formatContainerMarkers(containers, excluded, t);
    setMarkers(formattedContainers);
  }, [excluded, containers]);

  const customPopupContentConstructor = (marker: LeafletMarker) => {
    setSelectedMarker(marker);
    return popupContentConstructor(mapRef);
  };

  const classes = c(
    'ContainerMap',
    { 'ContainerMap--popup-open': popup !== null },
  );

  return (
    <div className={classes}>
      <LeafletMap
        ref={mapRef}
        center={center}
        zoom={MAP_ZOOM.DEFAULT}
        scrollWheelZoom={false}
        maxZoom={MAP_ZOOM.MAX}
        minZoom={MAP_ZOOM.MIN}
        touchZoom
      >
        <ZoomControl position="topright" />
        <LeafletCenterControl center={center} />
        <TileLayer
          attribution='Tiles by <a href="http://mapbox.com">MapBox</a>'
          url={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/256/{z}/{x}/{y}?access_token=${String(WIDGET_OPTIONS?.mapboxAccessToken)}`}
        />
        <LeafletMarkerLayer
          markers={markers}
          popupContentConstructor={customPopupContentConstructor}
          showClusters={process.env.NODE_ENV !== 'test'}
          focusPadding={{ x: 0, y: 200 }}
        />
        <LeafletMarkerLayer
          markers={[{
            id: 'home',
            geoLocation: {
              latitude: address.latitude,
              longitude: address.longitude,
            },
            body: '<div class="marker marker--home icon-after icon-home icon-variant-white"/>',
          }]}
          showPopup={false}
          showClusters={false}
        />
      </LeafletMap>
    </div>

  );
};

const MapComponent = (props: Omit<Props, 'setSelectedMarker'>) => {
  const [selectedMarker, setSelectedMarker] = React.useState<{ data: Container }>();

  return (
    <>
      {selectedMarker && (
        <LeafletPopup
          container={selectedMarker}
        />
      )}
      <LeafletMapRender
        setSelectedMarker={setSelectedMarker}
        {...props}
      />
    </>
  );
};

export default MapComponent;
